import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../../lib/api";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle } from "lucide-react";

const fmt = (n) => `€ ${(n || 0).toFixed(2)}`;
const today = new Date().toISOString().slice(0, 10);

const Row = ({ label, value, strong }) => (
  <div className="flex justify-between text-sm py-1.5 border-b border-border/50">
    <span className="text-muted-foreground">{label}</span>
    <span className={strong ? "font-semibold" : ""}>{value}</span>
  </div>
);

export default function CashClosingPanel() {
  const [date, setDate] = useState(today);
  const [calc, setCalc] = useState(null);
  const [counted, setCounted] = useState("");
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadHistory = useCallback(() => { api.get("/cash-closing").then(({ data }) => setHistory(data)).catch(() => {}); }, []);
  const loadPreview = useCallback((d) => { api.get(`/cash-closing/preview?date=${d}`).then(({ data }) => setCalc(data)).catch(() => setCalc(null)); }, []);
  useEffect(() => { loadPreview(date); }, [date, loadPreview]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const diff = calc && counted !== "" ? +(parseFloat(counted) - calc.expected_cash).toFixed(2) : null;

  const save = async () => {
    if (counted === "") return toast.error("Inserisci il contante contato");
    setSaving(true);
    try {
      await api.post("/cash-closing", { date, counted_cash: parseFloat(counted), note: "" });
      toast.success("Chiusura di cassa salvata"); setCounted(""); loadPreview(date); loadHistory();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5 rounded-xl" data-testid="cash-closing-calc">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-dash font-semibold">Cassa locale attesa</h3>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40 h-9" data-testid="closing-date-input" />
          </div>
          {calc && (
            <div>
              <Row label="Saldo iniziale cassa (apertura)" value={fmt(calc.opening_cash)} />
              <Row label="+ Incassi in contanti (vendite)" value={fmt(calc.cash_income)} />
              <Row label="− Spese pagate dalla cassa" value={fmt(calc.cash_expense)} />
              <Row label="− Spese fuori cassa (familiari)" value={fmt(calc.fuori_cassa_cash)} />
              <Row label="+ Versamenti in cassa" value={fmt(calc.transfer_in)} />
              <Row label="− Prelievi / uscite dalla cassa" value={fmt(calc.transfer_out)} />
              <Row label="= Contante atteso in cassa" value={fmt(calc.expected_cash)} strong />
              <div className="mt-4 space-y-2">
                <Label>Contante fisico contato (€)</Label>
                <Input type="number" step="0.01" value={counted} onChange={(e) => setCounted(e.target.value)} placeholder="Conta il denaro in cassa" data-testid="counted-cash-input" />
              </div>
              {diff !== null && (
                <div className={`mt-3 flex items-center gap-2 rounded-lg p-3 text-sm ${Math.abs(diff) < 0.005 ? "bg-emerald-500/10 text-emerald-600" : "bg-destructive/10 text-destructive"}`} data-testid="closing-difference">
                  {Math.abs(diff) < 0.005 ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                  {Math.abs(diff) < 0.005 ? "Cassa quadrata!" : `${diff > 0 ? "Eccedenza" : "Ammanco"} di ${fmt(Math.abs(diff))}`}
                </div>
              )}
              <Button onClick={save} disabled={saving} className="rounded-full mt-4 w-full" data-testid="save-closing-button">Salva chiusura</Button>
            </div>
          )}
        </Card>

        <Card className="p-5 rounded-xl" data-testid="cash-closing-other">
          <h3 className="font-dash font-semibold mb-3">Riepilogo altri conti (del giorno)</h3>
          {calc && (
            <div>
              <Row label="Portafoglio contanti (netto giornata)" value={fmt(calc.contante_day)} />
              <Row label="SumUp (netto giornata)" value={fmt(calc.sumup_day)} />
              <Row label="Banca (netto giornata)" value={fmt(calc.banca_day)} />
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-3">Portafoglio, SumUp e Banca sono mostrati come riepilogo (non richiedono conteggio fisico).</p>
        </Card>
      </div>

      <Card className="rounded-xl overflow-hidden">
        <Table data-testid="closings-history-table">
          <TableHeader><TableRow><TableHead>Data</TableHead><TableHead className="text-right">Atteso</TableHead><TableHead className="text-right">Contato</TableHead><TableHead className="text-right">Differenza</TableHead><TableHead className="text-right">SumUp</TableHead><TableHead className="text-right">Banca</TableHead><TableHead>Operatore</TableHead></TableRow></TableHeader>
          <TableBody>
            {history.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Nessuna chiusura registrata</TableCell></TableRow>}
            {history.map((h) => (
              <TableRow key={h.id} data-testid={`closing-row-${h.date}`}>
                <TableCell className="font-medium">{h.date}</TableCell>
                <TableCell className="text-right">{fmt(h.expected_cash)}</TableCell>
                <TableCell className="text-right">{fmt(h.counted_cash)}</TableCell>
                <TableCell className={`text-right font-medium ${Math.abs(h.difference || 0) < 0.005 ? "text-emerald-600" : "text-destructive"}`}>{h.difference > 0 ? "+" : ""}{fmt(h.difference)}</TableCell>
                <TableCell className="text-right">{fmt(h.sumup_day)}</TableCell>
                <TableCell className="text-right">{fmt(h.banca_day)}</TableCell>
                <TableCell className="text-muted-foreground">{h.operator || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
